import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Copy, Mail, Phone, MapPin, Send, Loader2, Trash2 } from "lucide-react";
import api from "@/lib/api";
import { PageHeader } from "@/components/admin/PageHeader";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { formatDateTime, formatMoney } from "@/lib/format";
import { toast } from "sonner";

const STATUSES = ["New", "Open", "Contacted", "Awaiting Response", "Referred to Agency", "Qualified", "Viewing Arranged", "Offer Made", "Converted", "Closed", "Unsuccessful", "Duplicate", "Spam", "Archived"];
const REFERRAL = ["Not Referred", "Ready to Refer", "Referred", "Agency Confirmed", "Qualified", "Transaction in Progress", "Successful", "Unsuccessful", "Commission Due", "Commission Invoiced", "Commission Paid"];

export default function EnquiryDetail() {
  const { id } = useParams();
  const [e, setE] = useState(null);
  const [note, setNote] = useState("");

  const load = () => api.get(`/admin/enquiries/${id}`).then((r) => setE(r.data)).catch(() => {});
  useEffect(() => { load(); }, [id]);

  const update = async (data) => { const r = await api.put(`/admin/enquiries/${id}`, data); setE((prev) => ({ ...prev, ...r.data })); toast.success("Updated"); };
  const addNote = async () => { if (!note) return; await api.post(`/admin/enquiries/${id}/note`, { text: note }); setNote(""); load(); toast.success("Note added"); };
  const copyEmail = (html) => { const tmp = document.createElement("div"); tmp.innerHTML = html; navigator.clipboard.writeText(tmp.innerText); toast.success("Email content copied"); };

  if (!e) return <div className="flex justify-center py-24"><Loader2 className="w-8 h-8 text-[#C81D35] animate-spin" /></div>;

  return (
    <div>
      <PageHeader title={e.full_name} subtitle={`Enquiry received ${formatDateTime(e.created_at)}`}
        action={<Link to="/admin/enquiries"><Button variant="outline" className="rounded-full gap-2"><ArrowLeft className="w-4 h-4" /> Back</Button></Link>} />

      <div className="grid lg:grid-cols-[1fr_340px] gap-6">
        <div className="space-y-6">
          <Card title="Contact details">
            <dl className="grid sm:grid-cols-2 gap-3 text-sm">
              <Row icon={Mail} label="Email" value={e.email} />
              <Row icon={Phone} label="Phone" value={e.phone} />
              <Row label="Preferred contact" value={e.preferred_contact} />
              <Row label="Preferred viewing" value={e.preferred_viewing_date} />
              <Row label="Position" value={e.buying_position} />
              <Row label="Consent" value={e.consent ? "Given" : "No"} />
              <Row label="Marketing" value={e.marketing_consent ? "Opted in" : "No"} />
              <Row label="Source" value={e.utm_source || "Direct"} />
            </dl>
            {e.message && <div className="mt-4 pt-4 border-t border-border"><p className="text-xs text-muted-foreground mb-1">Message</p><p className="text-sm text-[#111827]">{e.message}</p></div>}
          </Card>

          {e.property_title && (
            <Card title="Property">
              <p className="font-medium text-[#111827]">{e.property_title}</p>
              <p className="text-sm text-muted-foreground">{e.property_reference} · {e.property_location} · {formatMoney(e.property_price)}</p>
              {e.property_slug && <Link to={`/property/${e.property_slug}`} target="_blank" className="text-sm text-[#C81D35] font-semibold mt-1 inline-block">View property →</Link>}
            </Card>
          )}

          <Card title="Email previews">
            <p className="text-xs text-muted-foreground mb-3">These emails are generated and logged. Delivery is pending until Resend is connected.</p>
            <div className="flex flex-wrap gap-2">
              <EmailPreviewBtn label="Admin notification" email={e.admin_email_preview} onCopy={copyEmail} />
              <EmailPreviewBtn label="Visitor acknowledgement" email={e.ack_email_preview} onCopy={copyEmail} />
              {e.referral_email_preview && <EmailPreviewBtn label="Agency referral" email={e.referral_email_preview} onCopy={copyEmail} />}
            </div>
          </Card>

          <Card title="Notes & timeline">
            <div className="flex gap-2 mb-4"><Textarea rows={2} value={note} onChange={(ev) => setNote(ev.target.value)} placeholder="Add an internal note..." data-testid="note-input" /><Button onClick={addNote} className="rounded-full bg-[#111827] self-end">Add</Button></div>
            <div className="space-y-3">
              {(e.notes || []).map((n) => (
                <div key={n.id} className="text-sm bg-muted/50 rounded-lg p-3"><p className="text-[#111827]">{n.text}</p><p className="text-xs text-muted-foreground mt-1">{n.by} · {formatDateTime(n.at)}</p></div>
              ))}
              {(e.history || []).map((h, i) => (
                <div key={i} className="text-sm flex items-center gap-2 text-muted-foreground"><span className="w-1.5 h-1.5 rounded-full bg-[#C81D35]" /> {h.action} · {formatDateTime(h.at)}</div>
              ))}
            </div>
          </Card>
        </div>

        <div className="space-y-6">
          <Card title="Manage">
            <div className="space-y-4">
              <div className="space-y-1.5"><label className="text-xs font-medium text-muted-foreground">Status</label>
                <Select value={e.status} onValueChange={(v) => update({ status: v })}>
                  <SelectTrigger data-testid="enquiry-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5"><label className="text-xs font-medium text-muted-foreground">Referral status</label>
                <Select value={e.referral_status || "Not Referred"} onValueChange={(v) => update({ referral_status: v })}>
                  <SelectTrigger data-testid="referral-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{REFERRAL.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
          </Card>
          {e.agency_name && <Card title="Assigned agency"><p className="font-medium text-[#111827]">{e.agency_name}</p></Card>}
        </div>
      </div>
    </div>
  );
}

function Card({ title, children }) { return <div className="bg-white rounded-xl border border-border p-6"><h3 className="font-semibold text-[#111827] mb-4">{title}</h3>{children}</div>; }
function Row({ icon: Icon, label, value }) { if (!value) return null; return <div><dt className="text-xs text-muted-foreground flex items-center gap-1">{Icon && <Icon className="w-3 h-3" />} {label}</dt><dd className="font-medium text-[#111827]">{value}</dd></div>; }
function EmailPreviewBtn({ label, email, onCopy }) {
  return (
    <Dialog>
      <DialogTrigger asChild><Button variant="outline" size="sm" className="rounded-full gap-1.5" data-testid={`preview-${label}`}><Mail className="w-3.5 h-3.5" /> {label}</Button></DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{email?.subject}</DialogTitle></DialogHeader>
        <p className="text-xs text-muted-foreground">To: {email?.to || "—"} · <span className="text-amber-600 font-semibold">Pending Email Configuration</span></p>
        <div className="border border-border rounded-lg overflow-hidden mt-2" dangerouslySetInnerHTML={{ __html: email?.html }} />
        <Button onClick={() => onCopy(email?.html)} className="rounded-full bg-[#C81D35] hover:bg-[#a5162b] gap-2 w-fit"><Copy className="w-4 h-4" /> Copy content</Button>
      </DialogContent>
    </Dialog>
  );
}
